// src/pages/PublicHome.js
//
// The public-facing marketing home page. No login required. Shown to every
// visitor by default. There is NO staff login button here — the ERP login
// is deliberately private and only reachable via a hidden URL
// (/login/admin) so casual visitors never see an auth prompt.
// Authors & Books content is managed by staff with the manage_website
// permission (see pages/Website.js) and every visit here is counted so
// management can see how many people view the site.
import React, { useEffect, useState } from 'react';
import { getPublicBooks, trackPageView } from '../services/api';

const T = {
  bg: '#050b18', surface: '#0b1f3a', card: '#0f2742', border: '#1e3a5f',
  gold: '#00d4ff', goldLight: '#5be3ff', goldDim: '#1e88e5',
  text: '#F0F6FF', muted: '#93a8c4', success: '#22C55E',
};

const SERVICES = [
  { icon: '🖨️', title: 'Commercial Printing', desc: 'Flyers, brochures, business cards, banners and posters — full colour, fast turnaround.' },
  { icon: '📚', title: 'Book Printing & Publishing', desc: 'From manuscript to finished book, we print and help bring authors\u2019 work to readers.' },
  { icon: '👕', title: 'Branded Merchandise', desc: 'T-shirts, roll-up banners and branded materials for your business or event.' },
  { icon: '🚚', title: 'Delivery & Logistics', desc: 'Reliable dispatch and delivery once your order is packaged and ready.' },
];

export default function PublicHome() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('home').catch(() => {});
    getPublicBooks()
      .then(r => setBooks(r.data.data))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      {/* ── Nav ─────────────────────────────────────────────── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50, background: 'rgba(5,11,24,0.9)', backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 28px',
      }}>
        <div style={{ fontSize: '15px', fontWeight: 900, letterSpacing: '1.5px', color: T.gold }}>✦ MASTER SOLVER</div>
      </header>

      {/* ── Hero ────────────────────────────────────────────── */}
      <section style={{
        padding: '90px 24px 70px', textAlign: 'center',
        background: 'radial-gradient(circle at 50% 0%, #0b1f3a 0%, #050b18 65%)',
      }}>
        <div style={{ fontSize: '12px', letterSpacing: '3px', color: T.goldLight, fontWeight: 700, marginBottom: '18px', textTransform: 'uppercase' }}>
          Printing · Publishing · Branding
        </div>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 54px)', fontWeight: 900, margin: '0 0 18px', lineHeight: 1.15 }}>
          Solve Today, <span style={{ color: T.gold }}>Empower Tomorrow</span>
        </h1>
        <p style={{ maxWidth: '620px', margin: '0 auto', color: T.muted, fontSize: '16px', lineHeight: 1.7 }}>
          Master Solver is a full-service printing and publishing house — from business
          branding to bringing new books and authors to life.
        </p>
        <div style={{ marginTop: '34px', display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="#books" style={{
            background: `linear-gradient(135deg, ${T.goldDim}, ${T.gold})`, color: '#04121f', fontWeight: 800,
            padding: '13px 26px', borderRadius: '9px', textDecoration: 'none', fontSize: '14px',
          }}>Explore Our Books</a>
          <a href="#contact" style={{
            background: 'transparent', border: `1px solid ${T.border}`, color: T.text, fontWeight: 700,
            padding: '13px 26px', borderRadius: '9px', textDecoration: 'none', fontSize: '14px',
          }}>Get in Touch</a>
        </div>
      </section>

      {/* ── Services ────────────────────────────────────────── */}
      <section style={{ padding: '60px 24px', maxWidth: '1100px', margin: '0 auto' }}>
        <SectionHeading eyebrow="What we do" title="Our Services" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginTop: '32px' }}>
          {SERVICES.map(s => (
            <div key={s.title} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: '14px', padding: '24px' }}>
              <div style={{ fontSize: '26px', marginBottom: '12px' }}>{s.icon}</div>
              <div style={{ fontWeight: 800, fontSize: '15px', marginBottom: '8px' }}>{s.title}</div>
              <div style={{ color: T.muted, fontSize: '13px', lineHeight: 1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Authors & Books ─────────────────────────────────── */}
      <section id="books" style={{ padding: '60px 24px', background: T.surface, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <SectionHeading eyebrow="Authors & Books" title="Featured Authors & Titles We've Published" />
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: T.muted }}>Loading books…</div>
          ) : books.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: T.muted, fontSize: '14px' }}>
              Our authors & books showcase is coming soon — check back shortly.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '20px', marginTop: '34px' }}>
              {books.map(b => <BookCard key={b.id} book={b} />)}
            </div>
          )}
        </div>
      </section>

      {/* ── About ───────────────────────────────────────────── */}
      <section style={{ padding: '60px 24px', maxWidth: '820px', margin: '0 auto', textAlign: 'center' }}>
        <SectionHeading eyebrow="About us" title="Why Master Solver" center />
        <p style={{ color: T.muted, fontSize: '15px', lineHeight: 1.8, marginTop: '18px' }}>
          We combine dependable printing, careful order tracking and a genuine
          partnership with the authors and businesses we work with — so every
          job, from a single business card to a finished book, gets delivered
          right and on time.
        </p>
      </section>

      {/* ── Footer / Contact ────────────────────────────────── */}
      <footer id="contact" style={{ padding: '40px 24px', borderTop: `1px solid ${T.border}`, textAlign: 'center', color: T.muted, fontSize: '12.5px' }}>
        <div style={{ fontWeight: 800, color: T.gold, fontSize: '13px', letterSpacing: '1px', marginBottom: '8px' }}>✦ MASTER SOLVER</div>
        <div>Get in touch for printing, publishing and branding enquiries.</div>
        <div style={{ marginTop: '18px', opacity: 0.6 }}>© {new Date().getFullYear()} Master Solver. All rights reserved.</div>
      </footer>
    </div>
  );
}

function SectionHeading({ eyebrow, title, center }) {
  return (
    <div style={{ textAlign: center ? 'center' : 'left' }}>
      <div style={{ fontSize: '11px', letterSpacing: '2.5px', color: T.goldDim, fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>{eyebrow}</div>
      <h2 style={{ fontSize: '26px', fontWeight: 900, margin: 0 }}>{title}</h2>
    </div>
  );
}

function BookCard({ book }) {
  const initials = (book.title || '?').slice(0, 2).toUpperCase();
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {book.cover_image_url ? (
        <img src={book.cover_image_url} alt={book.title} style={{ width: '100%', height: '190px', objectFit: 'cover' }} />
      ) : (
        <div style={{
          height: '190px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `linear-gradient(135deg, ${T.goldDim}, ${T.gold})`, color: '#04121f', fontSize: '34px', fontWeight: 900,
        }}>{initials}</div>
      )}
      <div style={{ padding: '18px' }}>
        {book.is_featured ? (
          <div style={{ fontSize: '10px', color: T.gold, fontWeight: 800, letterSpacing: '1px', marginBottom: '6px', textTransform: 'uppercase' }}>★ Featured</div>
        ) : null}
        <div style={{ fontWeight: 800, fontSize: '15px', marginBottom: '4px' }}>{book.title}</div>
        <div style={{ fontSize: '12.5px', color: T.goldLight, marginBottom: '10px' }}>
          by {book.author_name}{book.published_year ? ` · ${book.published_year}` : ''}
        </div>
        {book.description && (
          <div style={{ fontSize: '12.5px', color: T.muted, lineHeight: 1.6 }}>{book.description}</div>
        )}
      </div>
    </div>
  );
}
